import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { TextAreaField, TextField } from "@/components/base/FormControls";

interface InvoiceRow {
  id: string;
  doc_type: string | null;
  show_logo: boolean | null;
  show_rnc: boolean | null;
  show_phone: boolean | null;
  show_address: boolean | null;
  show_cashier: boolean | null;
  show_customer: boolean | null;
  show_discount: boolean | null;
  show_tax: boolean | null;
  show_ncf: boolean | null;
  show_social: boolean | null;
  show_return_message: boolean | null;
  show_thanks: boolean | null;
  logo_size: number | null;
  header_text: string | null;
  footer_text: string | null;
  thanks_text: string | null;
  return_text: string | null;
}

type ToggleKey =
  | "show_logo"
  | "show_rnc"
  | "show_phone"
  | "show_address"
  | "show_cashier"
  | "show_customer"
  | "show_discount"
  | "show_tax"
  | "show_ncf"
  | "show_social"
  | "show_return_message"
  | "show_thanks";

const TOGGLES: { key: ToggleKey; label: string }[] = [
  { key: "show_logo", label: "Mostrar logo" },
  { key: "show_rnc", label: "Mostrar RNC" },
  { key: "show_phone", label: "Mostrar teléfono" },
  { key: "show_address", label: "Mostrar dirección" },
  { key: "show_cashier", label: "Mostrar cajero" },
  { key: "show_customer", label: "Mostrar cliente" },
  { key: "show_discount", label: "Mostrar descuento" },
  { key: "show_tax", label: "Mostrar impuestos" },
  { key: "show_ncf", label: "Mostrar NCF / e-CF" },
  { key: "show_social", label: "Mostrar redes sociales" },
  { key: "show_return_message", label: "Mostrar mensaje de devolución" },
  { key: "show_thanks", label: "Mostrar agradecimiento" },
];

export default function InvoiceTab() {
  const toast = useToast();
  const { profile } = useAuth();
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [texts, setTexts] = useState({
    logo_size: "120",
    header_text: "",
    footer_text: "",
    thanks_text: "",
    return_text: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("invoice_settings")
        .select(
          "id, doc_type, show_logo, show_rnc, show_phone, show_address, show_cashier, show_customer, show_discount, show_tax, show_ncf, show_social, show_return_message, show_thanks, logo_size, header_text, footer_text, thanks_text, return_text",
        );
      if (error) {
        toast.error("No se pudo cargar la configuración de facturas.");
        return;
      }
      const list = (data ?? []) as InvoiceRow[];
      setRows(list);
      const ref = list[0];
      if (ref) {
        setTexts({
          logo_size: String(ref.logo_size ?? 120),
          header_text: ref.header_text ?? "",
          footer_text: ref.footer_text ?? "",
          thanks_text: ref.thanks_text ?? "",
          return_text: ref.return_text ?? "",
        });
      }
    } catch {
      toast.error("No se pudo cargar la configuración de facturas.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const patchToggle = (docType: string, key: ToggleKey, value: boolean) => {
    setRows((prev) =>
      prev.map((row) => (row.doc_type === docType ? { ...row, [key]: value } : row)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const textPayload = {
        logo_size: Number.parseInt(texts.logo_size, 10) || 120,
        header_text: texts.header_text.trim() || null,
        footer_text: texts.footer_text.trim() || null,
        thanks_text: texts.thanks_text.trim() || null,
        return_text: texts.return_text.trim() || null,
        updated_at: new Date().toISOString(),
      };

      for (const row of rows) {
        const { error } = await supabase
          .from("invoice_settings")
          .update({
            show_logo: row.show_logo,
            show_rnc: row.show_rnc,
            show_phone: row.show_phone,
            show_address: row.show_address,
            show_cashier: row.show_cashier,
            show_customer: row.show_customer,
            show_discount: row.show_discount,
            show_tax: row.show_tax,
            show_ncf: row.show_ncf,
            show_social: row.show_social,
            show_return_message: row.show_return_message,
            show_thanks: row.show_thanks,
            ...textPayload,
          })
          .eq("id", row.id);
        if (error) {
          toast.error("No se pudo guardar la configuración de facturas.");
          return;
        }
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "configurar",
        entity: "invoice_settings",
        entityId: rows[0]?.id ?? "invoice_settings",
        newValue: { doc_types: rows.map((r) => r.doc_type) },
      });

      await load();
      toast.success("Configuración de facturas guardada.");
    } catch {
      toast.error("Ocurrió un error inesperado.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-lg"></i>
        Cargando configuración de facturas...
      </div>
    );
  }

  const posRow = rows.find((row) => row.doc_type === "pos");
  const a4Row = rows.find((row) => row.doc_type === "a4");

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-list-check-2 text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">
            Campos visibles por formato
          </h3>
        </div>

        <div className="overflow-hidden rounded-lg border border-background-200">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-background-200 bg-background-200/60 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
            <span>Campo</span>
            <span className="w-16 text-center">POS</span>
            <span className="w-16 text-center">A4</span>
          </div>
          <ul className="divide-y divide-background-200">
            {TOGGLES.map((toggle) => (
              <li key={toggle.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-2.5">
                <span className="text-sm text-foreground-800">{toggle.label}</span>
                <div className="flex w-16 justify-center">
                  <ToggleCell
                    checked={Boolean(posRow?.[toggle.key])}
                    onChange={(value) => posRow && patchToggle("pos", toggle.key, value)}
                  />
                </div>
                <div className="flex w-16 justify-center">
                  <ToggleCell
                    checked={Boolean(a4Row?.[toggle.key])}
                    onChange={(value) => a4Row && patchToggle("a4", toggle.key, value)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
            <i className="ri-text-block text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Textos y presentación A4</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:max-w-xs">
          <TextField
            label="Tamaño del logo (px)"
            name="logo_size"
            value={texts.logo_size}
            onChange={(value) => setTexts((prev) => ({ ...prev, logo_size: value.replace(/[^0-9]/g, "") }))}
            inputMode="numeric"
            placeholder="120"
          />
        </div>
        <TextAreaField
          label="Encabezado"
          name="header_text"
          value={texts.header_text}
          onChange={(value) => setTexts((prev) => ({ ...prev, header_text: value }))}
          placeholder="Texto que aparece en la parte superior de la factura A4"
          rows={2}
        />
        <TextAreaField
          label="Pie de página"
          name="footer_text"
          value={texts.footer_text}
          onChange={(value) => setTexts((prev) => ({ ...prev, footer_text: value }))}
          placeholder="Condiciones, datos bancarios u otra información"
          rows={2}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextAreaField
            label="Mensaje de agradecimiento"
            name="thanks_text"
            value={texts.thanks_text}
            onChange={(value) => setTexts((prev) => ({ ...prev, thanks_text: value }))}
            placeholder="¡Gracias por su compra!"
            rows={2}
          />
          <TextAreaField
            label="Mensaje de devolución"
            name="return_text"
            value={texts.return_text}
            onChange={(value) => setTexts((prev) => ({ ...prev, return_text: value }))}
            placeholder="Cambios y devoluciones dentro de 15 días con esta factura."
            rows={2}
          />
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
        >
          {saving ? (
            <i className="ri-loader-4-line animate-spin text-base"></i>
          ) : (
            <i className="ri-save-3-line text-base"></i>
          )}
          Guardar cambios
        </button>
      </div>
    </div>
  );
}

interface ToggleCellProps {
  checked: boolean;
  onChange: (value: boolean) => void;
}

function ToggleCell({ checked, onChange }: ToggleCellProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-accent-500" : "bg-background-300"
      }`}
    >
      <span
        className={`absolute h-5 w-5 rounded-full bg-background-50 transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}